/**
 * ============================================================================
 * UPCART V1/V2 COMPATIBLE INTEGRATION — Eureka Pet Co
 * ============================================================================
 *
 * FILE CONTENTS:
 *   PART 1: Checkout + Free Item Logic (was REPLACEMENT #1)
 *   PART 2: Cart Message / Taste Tester Logic (was REPLACEMENT #2)
 *
 * WHAT THIS HANDLES:
 *   - Free gift logic (Welcome Pack / Welcome Treat / bundles)
 *   - Mobile back-button (bfcache) state reset for iOS Safari + Android Chrome
 *   - Upcart V1 (legacy) AND V2 (current) class compatibility via UPCART_SEL
 *   - V2 PRIMARY: window.upcartOnCheckout / upcartOnCartOpen / upcartOnCartClosed
 *   - V1 FALLBACK: delegated event listener on document.body
 *   - All iPhone fallback strategies (fetch → XHR → jQuery)
 *   - Shopify cart API interception for instant free-item display
 *   - Taste Tester rewards message override
 *
 * UPCART V2 MIGRATION:
 *   When Upcart support confirms V2 class names, update UPCART_SEL map below.
 *   That's the single source of truth — no other code changes needed.
 *
 * ----------------------------------------------------------------------------
 * DEBUGGING (browser console, on any page with the cart drawer):
 *
 *   window.EP_UPCART_VERSION   -> confirms WHICH build of this file is live.
 *                                 undefined = an older asset is still serving.
 *
 *   await EP_giftDebug()       -> table of every cart line: variant title, sku,
 *                                 grams, whether it's a subscription, and the
 *                                 gift tier it resolved to. Also reports whether
 *                                 the drawer container was found and whether the
 *                                 free-gift row got injected.
 *
 *   window.EP_UPCART_DEBUG = true   -> live per-line logging as the cart changes.
 * ----------------------------------------------------------------------------
 *
 * CHANGE LOG:
 *   2026-06-12 — Added Starter Pack (SKU: EURDVKB800) to Welcome Pack logic.
 *                Two lines added: one in checkAndUpdateCart(), one in
 *                determineFreeItemVariantId(). Subscription-only, same as
 *                Variety Pack pattern.
 *
 *   2026-07-28 — E75 RANGE ADDED + FREE-GIFT LOGIC UNIFIED. (build -01)
 *
 *                PROBLEM: the two places that decided the free gift used
 *                DIFFERENT rules and had drifted apart:
 *                  - determineFreeItemVariantId() (runs at checkout) matched on
 *                    item.grams (850 = Treat, >850 = Pack).
 *                  - checkAndUpdateCart() (draws the row in the drawer) matched
 *                    on hardcoded variant titles ('800g','1.8kg','3.6kg','5.4kg').
 *                Result for E75: the checkout path WOULD add a Welcome Pack
 *                (2.25kg > 850g) but the drawer would show NO gift row, because
 *                '2.25kg' wasn't in the title list. Silent mismatch — and this is
 *                exactly the symptom to look for if the drawer preview is missing
 *                while checkout works: it means an older build is still serving.
 *
 *                FIX: both now call one shared matcher, giftTierForItem(), driven
 *                by the FREE_GIFT config block below. Add a future product by
 *                editing that block only — nothing else in this file.
 *
 *                E75 products added (all Welcome Pack):
 *                  Single Protein Wild Kangaroo      2.25kg / 4.5kg / 6.75kg
 *                  Turkey & Duck                     2.25kg / 4.5kg / 6.75kg
 *                  Grass-Fed Lamb & Wild Sardine     2.25kg / 4.5kg / 6.75kg
 *                  E75 Variety Pack  (SKU E75B3225)  3 x 2.25kg
 *                  E75 Starter Pack  (SKU E75B31)    3 x 1kg
 *
 *                NOTE: no E75 size maps to the Welcome TREAT — the smallest E75
 *                bag (2.25kg) sits well above the old 800g Treat tier, which
 *                matches the Skio PDP block's ep_treat_threshold_g (1800g).
 *                To put 2.25kg on the Treat instead, move '2.25kg' from
 *                PACK_TITLES to TREAT_TITLES below. No other edit needed.
 *
 *                KNOWN DATA ISSUE (fix in Shopify admin, not here): Turkey & Duck
 *                variant weights are shifted by one row — 2.25kg is set to 4.76kg,
 *                and BOTH 4.5kg and 6.75kg are set to 7.15kg. Lamb & Sardine and
 *                Kangaroo are correct (2.51 / 4.76 / 7.15). Titles are matched
 *                before weight here so gifts are unaffected, but shipping rates are.
 *
 *   2026-07-28 — DIAGNOSTICS ADDED. (build -02)
 *                No logic change. Added window.EP_UPCART_VERSION so the live
 *                build can be confirmed in one console line instead of searching
 *                the asset source, window.EP_giftDebug() to dump the per-line
 *                gift decision, an optional EP_UPCART_DEBUG logging flag, and a
 *                short retry in addCartItemHTML() for the case where the drawer
 *                container hasn't rendered its item list yet.
 *
 * LOAD INSTRUCTIONS (in theme.liquid):
 *   <script src="{{ 'upcart-integration.js' | asset_url }}" defer="defer"></script>
 * ============================================================================
 */

(function () {
  'use strict';

  /* ==========================================================================
   * BUILD MARKER — bump this string whenever this file is edited.
   * Lets you confirm in one console line which build is actually serving.
   * ========================================================================== */
  window.EP_UPCART_VERSION = '2026-07-28-e75-02';

  // Set window.EP_UPCART_DEBUG = true in the console for live decision logging.
  function epLog() {
    if (!window.EP_UPCART_DEBUG) return;
    try {
      console.log.apply(console, ['[EP gift]'].concat(Array.prototype.slice.call(arguments)));
    } catch (e) { /* ignore */ }
  }

  /* ==========================================================================
   * UPCART SELECTOR MAP — UPDATE HERE ONCE WHEN V2 NAMES ARE CONFIRMED
   * Each value matches BOTH V1 (legacy) and V2 (current) class names.
   * Comma-separated selectors = OR
   * ========================================================================== */
  const UPCART_SEL = {
    cartPopup:             '#CartPopup',
    cartBody:              '.upcart-cart-body, .upcart-public-cart-body',
    productsSection:       '.upcart-products-section, .upcart-public-cart-items',
    productItem:           '.upcart-product-item, .upcart-public-component-product-tile',
    productTitle:          '.upcart-product-title, .upcart-public-component-product-tile__product-title',
    productTitleLink:      '.upcart-product-title-link, .upcart-public-component-product-tile__title-link',
    itemProperty:          '.upcart-item-property, .upcart-public-product-properties__item',
    rewards:               '.upcart-rewards, .upcart-public-rewards',
    rewardsMessage:        '.upcart-rewards-message, .upcart-public-rewards__message',
    rewardsBar:            '.upcart-rewards-bar-background, .upcart-public-component-segmented-progress-bar',
    rewardsTiersContainer: '.styles_TieredRewards__tiersContainer__ZMHzn, .upcart-public-component-segmented-progress-bar__segments-container',
    removeButton:          '.styles_TrashButton__deleteButton__8uIHq, .upcart-public-component-product-tile__remove-button',
    checkoutButton:        '.upcart-checkout-button, .upcart-public-checkout-button',
    itemBottomRow:         '.Stack.Stack--distributionEqualSpacing, .upcart-public-component-product-tile__controls'
  };

  // Expose globally so other inline scripts in theme.liquid can reference it if needed
  window.UPCART_SEL = UPCART_SEL;

  /* ==========================================================================
   * FREE GIFT CONFIG — THE ONLY PLACE TO EDIT WHEN PRODUCTS CHANGE
   * ==========================================================================
   *
   * Matching order for every cart line (subscription lines only):
   *   1. BUNDLE_SKUS   — exact SKU match  -> that tier
   *   2. TREAT_TITLES  — variant title    -> 'treat'
   *   3. PACK_TITLES   — variant title    -> 'pack'
   *   4. weight fallback (grams)          -> 'pack' / 'treat' / nothing
   *
   * The weight fallback exists ONLY so a brand-new size that nobody added to the
   * lists above still gets a gift instead of silently getting none. Always add
   * the real title to the lists — don't rely on the fallback.
   *
   * ONE-TIME (non-subscription) purchases never get a free gift. That is
   * enforced in giftTierForItem() by the selling_plan_allocation check.
   * ========================================================================== */
  const FREE_GIFT = {
    // Shopify variant IDs of the free products themselves
    PACK_VARIANT_ID:  '46322042798364',   // Free Eureka Welcome Pack
    TREAT_VARIANT_ID: '46460357869852',   // Free Eureka Welcome Treat

    // Bundles are matched on SKU because their variant titles ("3 x 2.25kg",
    // "3 x 1kg") describe a multipack, not a single bag weight.
    BUNDLE_SKUS: {
      'EURDVKB-18-2': 'pack',   // E90 Variety Pack  (3 x 1.8kg)
      'EURDVKB800':   'pack',   // E90 Starter Pack  (3 x 800g)
      'E75B3225':     'pack',   // E75 Variety Pack  (3 x 2.25kg)  [draft at time of writing]
      'E75B31':       'pack'    // E75 Starter Pack  (3 x 1kg)     [draft at time of writing]
    },

    // Single-bag variant titles -> Welcome TREAT (the smaller gift)
    TREAT_TITLES: [
      '800g'                    // E90 small bag
    ],

    // Single-bag variant titles -> Welcome PACK (the larger gift)
    PACK_TITLES: [
      '1.8kg', '3.6kg', '5.4kg',      // E90 range
      '2.25kg', '4.5kg', '6.75kg'     // E75 range (Kangaroo / Turkey & Duck / Lamb & Sardine)
    ],

    // Weight fallback thresholds, in grams. Only used when the title above
    // doesn't match anything. Mirrors the old ">850g = Pack" behaviour.
    PACK_MIN_GRAMS:  1000,
    TREAT_MIN_GRAMS: 800,

    // Row content injected into the drawer for each tier
    ROWS: {
      pack: {
        domId: 'welcome-pack-id',
        title: 'Free Eureka Welcome Pack',
        image: 'https://cdn.shopify.com/s/files/1/0842/3135/6715/files/extended_image005.jpg?v=1723109134'
      },
      treat: {
        domId: 'welcome-treat-id',
        title: 'Free Eureka Welcome Treat',
        image: 'https://cdn.shopify.com/s/files/1/0842/3135/6715/files/extended_image005_book_goathorn.jpg?v=1723109069'
      }
    }
  };

  // Every free-gift variant ID, used when clearing the cart of stale gifts.
  const FREE_ITEM_IDS = [FREE_GIFT.TREAT_VARIANT_ID, FREE_GIFT.PACK_VARIANT_ID];

  // Taste Tester / sample SKU — drives the rewards-message override in PART 2.
  // If the new E75/E90 taste tester products use different SKUs, add them here.
  const TASTE_TESTER_SKUS = ['RFDRK000789'];

  // Normalise a variant title for comparison: "2.25 KG" -> "2.25kg"
  function normTitle(t) {
    return (t == null ? '' : String(t)).trim().toLowerCase().replace(/\s+/g, '');
  }

  const TREAT_TITLES_N = FREE_GIFT.TREAT_TITLES.map(normTitle);
  const PACK_TITLES_N  = FREE_GIFT.PACK_TITLES.map(normTitle);

  /**
   * Decide which free gift (if any) a single cart line earns.
   * @returns {'pack'|'treat'|null}
   *
   * Used by BOTH the drawer display and the checkout handler, so the two can
   * never disagree again.
   */
  function giftTierForItem(item) {
    if (!item) return null;

    // Subscription lines only — one-time purchases never earn a gift.
    if (!item.selling_plan_allocation) {
      epLog('skip (not a subscription):', item.variant_title, item.sku);
      return null;
    }

    // Never let a free gift line earn another free gift.
    if (FREE_ITEM_IDS.indexOf(String(item.variant_id)) !== -1) {
      epLog('skip (is a free gift line):', item.variant_title);
      return null;
    }

    // 1. Bundle SKU
    const sku = (item.sku == null ? '' : String(item.sku)).trim();
    if (sku && Object.prototype.hasOwnProperty.call(FREE_GIFT.BUNDLE_SKUS, sku)) {
      epLog('matched bundle SKU', sku, '->', FREE_GIFT.BUNDLE_SKUS[sku]);
      return FREE_GIFT.BUNDLE_SKUS[sku];
    }

    // 2 + 3. Variant title
    const title = normTitle(item.variant_title);
    if (title) {
      if (TREAT_TITLES_N.indexOf(title) !== -1) {
        epLog('matched treat title', item.variant_title);
        return 'treat';
      }
      if (PACK_TITLES_N.indexOf(title) !== -1) {
        epLog('matched pack title', item.variant_title);
        return 'pack';
      }
    }

    // 4. Weight fallback for sizes not yet listed above
    const grams = Number(item.grams) || 0;
    if (grams >= FREE_GIFT.PACK_MIN_GRAMS) {
      epLog('title "' + item.variant_title + '" not listed — weight fallback', grams, '-> pack');
      return 'pack';
    }
    if (grams >= FREE_GIFT.TREAT_MIN_GRAMS) {
      epLog('title "' + item.variant_title + '" not listed — weight fallback', grams, '-> treat');
      return 'treat';
    }

    epLog('no match:', item.variant_title, '| sku', sku, '| grams', grams);
    return null;
  }

  /**
   * Walk the whole cart and work out which single gift it earns.
   * Pack always beats Treat when both are present.
   * @returns {'pack'|'treat'|null}
   */
  function giftTierForCart(cartData) {
    if (!cartData || !cartData.items || !cartData.items.length) return null;

    let wantsPack = false;
    let wantsTreat = false;

    cartData.items.forEach(function (item) {
      const tier = giftTierForItem(item);
      if (tier === 'pack') wantsPack = true;
      else if (tier === 'treat') wantsTreat = true;
    });

    if (wantsPack) return 'pack';
    if (wantsTreat) return 'treat';
    return null;
  }

  function isTasteTesterItem(item) {
    const sku = (item && item.sku != null) ? String(item.sku).trim() : '';
    return sku !== '' && TASTE_TESTER_SKUS.indexOf(sku) !== -1;
  }

  /* ==========================================================================
   * DIAGNOSTIC — run `await EP_giftDebug()` in the console.
   * Shows exactly what the matcher sees for every line in the live cart, plus
   * whether the drawer container exists and whether the gift row was injected.
   * ========================================================================== */
  window.EP_giftDebug = async function () {
    const cart = await fetchCartData();
    if (!cart) {
      console.warn('[EP gift] could not read /cart.js');
      return null;
    }

    const rows = cart.items.map(function (i) {
      return {
        product:      i.product_title,
        variant:      i.variant_title,
        sku:          i.sku,
        grams:        i.grams,
        subscription: !!i.selling_plan_allocation,
        tier:         giftTierForItem(i) || '— none —'
      };
    });

    try { console.table(rows); } catch (e) { console.log(rows); }

    console.log('[EP gift] build            :', window.EP_UPCART_VERSION);
    console.log('[EP gift] cart tier        :', giftTierForCart(cart) || 'none');
    console.log('[EP gift] drawer container :', document.querySelector(UPCART_SEL.productsSection) || 'NOT FOUND');
    console.log('[EP gift] injected row     :', document.querySelector('.sg-injected-free-item') || 'not present');

    return rows;
  };

  // ════════════════════════════════════════════════════════════════════════════
  // ██  PART 1: CHECKOUT + FREE ITEM LOGIC  ██
  // ════════════════════════════════════════════════════════════════════════════

  let lastCartHtml = '';

  // ── Device Detection ──────────────────────────────────────────────────────
  function isIOSDevice() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }
  function isIPhone() {
    return /iPhone/.test(navigator.userAgent) && !window.MSStream;
  }
  function getIOSDelay(baseDelay) {
    return isIOSDevice() ? baseDelay * 2 : baseDelay;
  }
  function executeWithUserInteraction(operation) {
    if (isIPhone()) {
      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          try { resolve(await operation()); }
          catch (error) { reject(error); }
        }, 0);
      });
    } else {
      return operation();
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CART API INTERCEPTOR
  // Fires checkAndUpdateCart() the moment any Shopify cart endpoint is hit.
  // Operates on Shopify endpoints, not Upcart DOM — version-agnostic.
  // ════════════════════════════════════════════════════════════════════════════
  (function initCartApiInterceptor() {
    const CART_API_PATTERNS = ['/cart/add', '/cart/change', '/cart/update'];
    let _pendingUpdate = null;

    function isCartApiCall(url) {
      if (!url) return false;
      const urlStr = typeof url === 'string' ? url : url.toString();
      return CART_API_PATTERNS.some(pattern => urlStr.includes(pattern));
    }

    function triggerCartDisplayUpdate() {
      if (_pendingUpdate) clearTimeout(_pendingUpdate);
      _pendingUpdate = setTimeout(() => {
        _pendingUpdate = null;
        checkAndUpdateCart();
      }, 300);
    }

    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
      const method = (init && init.method) ? init.method.toUpperCase() : 'GET';
      if (method === 'POST' && isCartApiCall(url)) {
        return originalFetch.apply(this, arguments).then(response => {
          triggerCartDisplayUpdate();
          return response;
        });
      }
      return originalFetch.apply(this, arguments);
    };

    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._sgMethod = method;
      this._sgUrl = url;
      return originalXHROpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (this._sgMethod && this._sgMethod.toUpperCase() === 'POST' && isCartApiCall(this._sgUrl)) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) triggerCartDisplayUpdate();
        });
      }
      return originalXHRSend.apply(this, arguments);
    };

    function hookJQueryAjax() {
      if (typeof $ !== 'undefined' && $.ajaxPrefilter) {
        $.ajaxPrefilter(function (options) {
          if (options.type && options.type.toUpperCase() === 'POST' && isCartApiCall(options.url)) {
            const originalSuccess = options.success;
            options.success = function () {
              if (originalSuccess) originalSuccess.apply(this, arguments);
              triggerCartDisplayUpdate();
            };
          }
        });
      }
    }

    if (typeof $ !== 'undefined') hookJQueryAjax();
    else document.addEventListener('DOMContentLoaded', hookJQueryAjax);
  })();

  // ── Cart Display Logic — uses UPCART_SEL throughout ───────────────────────
  function checkAndUpdateCart() {
    setTimeout(() => {
      fetchCartData()
        .then((cart) => {
          if (!cart) return;

          // ── Free gift tier: ONE shared decision, same rules the checkout
          //    handler uses. See giftTierForItem() / FREE_GIFT config above.
          const tier = giftTierForCart(cart);
          epLog('cart tier ->', tier || 'none');

          let hasTasteTester = false;
          cart.items.forEach((cartItem) => {
            if (isTasteTesterItem(cartItem)) hasTasteTester = true;
          });

          setTimeout(() => {
            var divObject = document.querySelector(UPCART_SEL.rewards);
            var divCustMsg = document.getElementById('sg_upcart_drawer_free_ship_msg');

            if (divObject && divCustMsg) {
              if (hasTasteTester) {
                divObject.style.display = 'none';
                divCustMsg.style.display = 'block';
              } else {
                divCustMsg.style.display = 'none';
                divObject.style.display = 'block';
              }
            }

            // Draw exactly one gift row (or none), never both.
            if (tier === 'pack') {
              addCartItemHTML(
                FREE_GIFT.ROWS.pack.domId,
                FREE_GIFT.ROWS.pack.title,
                FREE_GIFT.ROWS.pack.image,
                0
              );
              removeCartItemHTML(FREE_GIFT.ROWS.treat.domId);
            } else if (tier === 'treat') {
              addCartItemHTML(
                FREE_GIFT.ROWS.treat.domId,
                FREE_GIFT.ROWS.treat.title,
                FREE_GIFT.ROWS.treat.image,
                0
              );
              removeCartItemHTML(FREE_GIFT.ROWS.pack.domId);
            } else {
              removeCartItemHTML(FREE_GIFT.ROWS.pack.domId);
              removeCartItemHTML(FREE_GIFT.ROWS.treat.domId);
            }
          }, 100);
        })
        .catch(() => { /* swallow */ });
    }, 100);

    setTimeout(() => {
      const propertyTags = document.querySelectorAll(UPCART_SEL.itemProperty);
      propertyTags.forEach((tag) => {
        const text = tag.textContent || tag.innerText;
        const weeksIndex = text.indexOf('Weeks');
        if (weeksIndex !== -1) {
          const updatedText = 'ship every ' + text.substring(0, weeksIndex) + 'weeks';
          tag.textContent = updatedText;
        }
      });

      const cartItems = document.querySelectorAll(UPCART_SEL.productItem);
      cartItems.forEach((item) => {
        const titleElement = item.querySelector(UPCART_SEL.productTitle);
        if (titleElement && titleElement.textContent.includes('100% off')) {
          titleElement.textContent = titleElement.textContent
            .replace('(100% off)', '')
            .trim();
          titleElement.classList.add('upcart-bogo-title');

          const deleteButtonContainer = item.querySelector(UPCART_SEL.removeButton);
          const deleteButtonParent = deleteButtonContainer && deleteButtonContainer.parentElement;

          if (deleteButtonContainer && deleteButtonParent && !deleteButtonParent.querySelector('.upcart_free_')) {
            deleteButtonContainer.remove();
            const priceElement = document.createElement('div');
            priceElement.className = 'upcart_free_';
            priceElement.innerHTML = `
                            <span class="upcart_price">$0.00</span>
                            <div class="upcart_free">FREE</div>
                        `;
            deleteButtonParent.appendChild(priceElement);
          }

          const bottomRow = item.querySelector(UPCART_SEL.itemBottomRow);
          if (bottomRow) bottomRow.remove();
        }
      });

      setTimeout(() => {
        document.querySelectorAll(UPCART_SEL.itemProperty).forEach((div) => {
          if (div.textContent.toLowerCase().includes('bogos')) {
            div.style.setProperty('display', 'none', 'important');
          }
        });
      }, 200);
    }, 300);
  }

  // ── Cart Item HTML Helpers (V1/V2 dual-class injection) ──────────────────
  function addCartItemHTML(id, title, imageUrl, price, _attempt) {
    if (document.getElementById(id)) return;

    const cartItemsSection = document.querySelector(UPCART_SEL.productsSection);
    if (!cartItemsSection) {
      // Drawer hasn't rendered its item list yet. Retry shortly — the 100ms
      // poll will also catch it eventually, this just tightens the window.
      const attempt = _attempt || 0;
      epLog('drawer container not found, retry', attempt + 1);
      if (attempt < 3) {
        setTimeout(function () {
          addCartItemHTML(id, title, imageUrl, price, attempt + 1);
        }, 300);
      }
      return;
    }

    const cartItemHTML = `
            <div class="styles_ProductRow__product__AokJ5 upcart-product-item upcart-public-component-product-tile sg-injected-free-item" id="${id}">
                <div class="Stack Stack--distributionLeading Stack--spacingLoose upcart-public-component-product-tile__content">
                    <div class="Stack__item">
                        <div class="upcart-product-image-wrapper upcart-public-component-product-tile__image-wrapper styles_ProductRow__imageWrapper__CgzZq">
                            <img src="${imageUrl}" alt="${title}" class="upcart-public-component-product-tile__image">
                        </div>
                    </div>
                    <div class="Stack__item Stack__item--fill upcart-public-component-product-tile__info">
                        <div class="styles_ProductRow__productHeading__vnxnN upcart-public-component-product-tile__header">
                            <div class="styles_ProductRow__productTitleAndVariant__On413">
                                <h3 class="upcart-product-title upcart-public-component-product-tile__product-title UpcartDesignSettings__cartTextColor styles_ProductRow__productTitle__gcycG upcart_free_title">${title}</h3>
                            </div>
                            <div>
                                <div class="Stack Stack--distributionEqualSpacing Stack--alignmentCenter">
                                    <div class="Stack__item" style="margin-left: auto;">
                                        <div class="styles_ProductRow__price__Ta93X upcart-public-component-product-tile__product-pricing">
                                            <div class="upcart-item-prices-row styles_ProductRow__priceRow__BONFQ">
                                                <span class="upcart-do-not-modify-this-node-please-only-modify-child-node">
                                                    <span class="upcart-item-price upcart-public-component-product-tile__price UpcartDesignSettings__cartTextColor styles_ProductRow__ProductPrice__MuDEq upcart_free_">$${price}.00 <div class="upcart_free">FREE</div></span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    cartItemsSection.insertAdjacentHTML('beforeend', cartItemHTML);
    epLog('injected row:', id);
  }

  function removeCartItemHTML(id) {
    const cartItem = document.getElementById(id);
    if (cartItem) cartItem.remove();
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CHECKOUT PROCESSING GUARD (module-level — survives bfcache reset)
  // ════════════════════════════════════════════════════════════════════════════
  let _isCheckoutProcessing = false;
  let _checkoutProcessingTimer = null;

  function resetCheckoutProcessing() {
    _isCheckoutProcessing = false;
    if (_checkoutProcessingTimer) {
      clearTimeout(_checkoutProcessingTimer);
      _checkoutProcessingTimer = null;
    }
  }

  function checkAndRemoveHref() {
    const upcartCheckoutButton = document.querySelector(UPCART_SEL.checkoutButton);
    if (upcartCheckoutButton && upcartCheckoutButton.hasAttribute('href')) {
      upcartCheckoutButton.removeAttribute('href');
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // CHECKOUT INTERCEPTION — V1 + V2 dual path
  //   V2 PRIMARY: window.upcartOnCheckout API hook (set further below)
  //   V1 FALLBACK: Delegated event listener on document.body (capture phase)
  // ════════════════════════════════════════════════════════════════════════════
  (function attachDelegatedCheckoutListener() {
    function isUpcartCheckoutButton(target) {
      if (!target || !target.matches) return null;
      try {
        if (target.matches(UPCART_SEL.checkoutButton)) return target;
        if (target.closest) return target.closest(UPCART_SEL.checkoutButton);
      } catch (e) { /* ignore selector errors */ }
      return null;
    }

    async function delegatedCheckoutHandler(event) {
      const target = isUpcartCheckoutButton(event.target);
      if (!target) return;

      if (target.hasAttribute('href')) target.removeAttribute('href');

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (_isCheckoutProcessing) return;
      _isCheckoutProcessing = true;

      try {
        await handleCheckout(event);
      } catch (error) {
        resetCheckoutProcessing();
      } finally {
        _checkoutProcessingTimer = setTimeout(resetCheckoutProcessing, 2000);
      }
    }

    document.addEventListener('click', delegatedCheckoutHandler, true);
    if (isIPhone()) {
      document.addEventListener('touchend', delegatedCheckoutHandler, true);
    }
  })();

  // ── Polling ───────────────────────────────────────────────────────────────
  function pollForCartChanges() {
    const cartItemsContainer = document.querySelector(UPCART_SEL.productsSection);
    if (cartItemsContainer) {
      const currentCartHtml = cartItemsContainer.innerHTML;
      if (currentCartHtml !== lastCartHtml) {
        lastCartHtml = currentCartHtml;
        checkAndUpdateCart();
      }
    }
    checkAndRemoveHref();

    const pollInterval = isIOSDevice() ? 200 : 100;
    setTimeout(pollForCartChanges, pollInterval);
  }

  pollForCartChanges();

  // ── Overflow ─────────────────────────────────────────────────────────────
  function removeOverflowHidden() {
    const bodyElement = document.getElementById('ep_body');
    if (bodyElement && bodyElement.classList.contains('overflow-hidden')) {
      bodyElement.classList.remove('overflow-hidden');
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // V2 PRIMARY CHECKOUT HOOK — window.upcartOnCheckout
  // Returning false blocks Upcart's default redirect.
  // ════════════════════════════════════════════════════════════════════════════
  window.upcartOnCheckout = function () {
    if (_isCheckoutProcessing) return false;
    _isCheckoutProcessing = true;

    handleCheckout({})
      .catch(() => { resetCheckoutProcessing(); })
      .finally(() => {
        _checkoutProcessingTimer = setTimeout(resetCheckoutProcessing, 2000);
      });

    return false;
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (isIPhone()) {
      const style = document.createElement('style');
      style.textContent = `
                .upcart-checkout-button,
                .upcart-public-checkout-button {
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    -khtml-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                    cursor: pointer;
                }
            `;
      document.head.appendChild(style);

      const initTimes = [3000, 6000, 9000, 12000];
      initTimes.forEach((time) => { setTimeout(checkAndRemoveHref, time); });

      setTimeout(() => { if (window.upcartRefreshCart) window.upcartRefreshCart(); }, 2000);
      setTimeout(() => { if (window.upcartRefreshCart) window.upcartRefreshCart(); }, 5000);
    } else {
      setTimeout(checkAndRemoveHref, 5000);
      setTimeout(checkAndRemoveHref, 8000);
      setTimeout(checkAndRemoveHref, 10000);
      setTimeout(() => { if (window.upcartRefreshCart) window.upcartRefreshCart(); }, 3000);
    }
  });

  // ════════════════════════════════════════════════════════════════════════════
  // MOBILE BACK-BUTTON / BFCACHE HANDLING
  // Reset checkout state when iOS Safari / Android Chrome restores from bfcache
  // ════════════════════════════════════════════════════════════════════════════
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      resetCheckoutProcessing();

      const checkoutBtn = document.querySelector(UPCART_SEL.checkoutButton);
      if (checkoutBtn) {
        checkoutBtn.classList.remove('processing');
        if (checkoutBtn.innerHTML.indexOf('loading-icon') !== -1 ||
          checkoutBtn.innerHTML.indexOf('Loading') !== -1) {
          checkoutBtn.innerHTML = 'CHECKOUT';
        }
        checkoutBtn.removeAttribute('data-processing');
      }

      if (window.upcartRefreshCart) window.upcartRefreshCart();

      setTimeout(checkAndRemoveHref, 200);
      setTimeout(checkAndRemoveHref, 800);
    }
  });

  // Android Chrome backup
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      resetCheckoutProcessing();

      const checkoutBtn = document.querySelector(UPCART_SEL.checkoutButton);
      if (checkoutBtn) {
        checkoutBtn.classList.remove('processing');
        if (checkoutBtn.innerHTML.indexOf('loading-icon') !== -1 ||
          checkoutBtn.innerHTML.indexOf('Loading') !== -1) {
          checkoutBtn.innerHTML = 'CHECKOUT';
        }
        checkoutBtn.removeAttribute('data-processing');
      }
    }
  });

  document.addEventListener('DOMContentLoaded', async () => {
    const cartData = await fetchCartData();
    if (!cartData) return;
    await removeExistingFreeItems();
    if (window.upcartRefreshCart) window.upcartRefreshCart();
  });


  // ════════════════════════════════════════════════════════════════════════════
  // handleCheckout — FIXED: always remove + always add (no skip logic)
  //
  // OLD BUG: skipping re-add when item already in cart caused free gift to
  // disappear on 2nd checkout click after mobile back-button.
  // FIX: always remove all existing free items, then always add correct one.
  // ════════════════════════════════════════════════════════════════════════════
  async function handleCheckout(event) {
    const upcartCheckoutButton = document.querySelector(UPCART_SEL.checkoutButton);

    let originalText = null;
    if (upcartCheckoutButton) {
      originalText = upcartCheckoutButton.innerHTML;
      if (originalText.indexOf('loading-icon') !== -1 || originalText.indexOf('Loading') !== -1) {
        originalText = 'CHECKOUT';
      }
      upcartCheckoutButton.innerHTML = '<span class="loading-icon"></span> Loading...';
      upcartCheckoutButton.classList.add('processing');
    }

    return executeWithUserInteraction(async () => {
      try {
        const cartData = await fetchCartData();
        if (!cartData) {
          if (upcartCheckoutButton && originalText !== null) {
            upcartCheckoutButton.innerHTML = originalText;
            upcartCheckoutButton.classList.remove('processing');
          }
          resetCheckoutProcessing();
          return;
        }

        const freeItemVariantId = determineFreeItemVariantId(cartData);

        const existingFreeItems = FREE_ITEM_IDS.filter(variantId =>
          cartData.items.some(item => item.variant_id.toString() === variantId)
        );

        // STEP 1: Always remove ALL existing free items (clean slate for back-button case)
        if (existingFreeItems.length > 0) {
          await Promise.all(existingFreeItems.map(id => removeItem(id)));
        }

        // STEP 2: Always add the correct free item (NO skip logic — that was the bug)
        if (freeItemVariantId) {
          await addFreeItemDirect(freeItemVariantId);
        }

        const checkoutDelay = isIPhone() ? 200 : 100;
        setTimeout(() => { redirectToCheckout(); }, checkoutDelay);

      } catch (error) {
        if (upcartCheckoutButton && originalText !== null) {
          upcartCheckoutButton.innerHTML = originalText;
          upcartCheckoutButton.classList.remove('processing');
        }
        resetCheckoutProcessing();
      }
    });
  }

  async function addFreeItemDirect(freeItemVariantId) {
    let addSuccess = false;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({ id: freeItemVariantId, quantity: 1 }),
      });
      if (response.ok) addSuccess = true;
      else throw new Error(`Fetch response not ok: ${response.status}`);
    } catch (fetchError) { /* fall through */ }

    if (!addSuccess) {
      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', '/cart/add.js', true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Accept', 'application/json');
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) { addSuccess = true; resolve(); }
            else reject(new Error(`XHR failed: ${xhr.statusText}`));
          };
          xhr.onerror = () => reject(new Error('XHR network error'));
          xhr.send(JSON.stringify({ id: freeItemVariantId, quantity: 1 }));
        });
      } catch (xhrError) { /* fall through */ }
    }

    if (!addSuccess && typeof $ !== 'undefined') {
      try {
        await new Promise((resolve, reject) => {
          $.ajax({
            url: '/cart/add.js',
            type: 'POST',
            dataType: 'json',
            contentType: 'application/json',
            data: JSON.stringify({ id: freeItemVariantId, quantity: 1 }),
            success: function () { addSuccess = true; resolve(); },
            error: function (xhr, status, error) { reject(new Error(`jQuery failed: ${error}`)); },
          });
        });
      } catch (jqueryError) { /* fall through */ }
    }

    if (!addSuccess) {
      epLog('FAILED to add free item', freeItemVariantId, '— does that variant exist on this shop?');
    }
  }

  function redirectToCheckout() {
    if (isIPhone()) {
      try { window.location.href = '/checkout'; }
      catch (e) {
        try {
          const link = document.createElement('a');
          link.href = '/checkout';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (e2) {
          const form = document.createElement('form');
          form.method = 'GET';
          form.action = '/checkout';
          form.style.display = 'none';
          document.body.appendChild(form);
          form.submit();
        }
      }
    } else {
      if (document.hasFocus()) window.location.href = '/checkout';
      else setTimeout(() => { window.location.href = '/checkout'; }, 300);
    }
  }

  // ── fetchCartData (with Cache-Control: no-cache for bfcache freshness) ───
  async function fetchCartData() {
    try {
      let cartData;
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache',
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch cart data: ${response.statusText}`);
        cartData = await response.json();
      } catch (fetchError) {
        cartData = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', '/cart.js', true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.setRequestHeader('Cache-Control', 'no-cache');
          xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
              try { resolve(JSON.parse(xhr.responseText)); }
              catch (parseError) { reject(new Error('Failed to parse cart data')); }
            } else reject(new Error(`Failed to fetch cart data: ${xhr.statusText}`));
          };
          xhr.onerror = () => reject(new Error('Network error fetching cart data'));
          xhr.send();
        });
      }
      return cartData;
    } catch (error) {
      try {
        const cartData = await new Promise((resolve, reject) => {
          $.ajax({
            url: '/cart.js',
            type: 'GET',
            dataType: 'json',
            cache: false,
            success: function (data) { resolve(data); },
            error: function (xhr, status, error) { reject(error); },
          });
        });
        return cartData;
      } catch (jqueryError) { return null; }
    }
  }

  async function isItemInCart(variantId) {
    const cartData = await fetchCartData();
    return cartData && cartData.items.some((item) => item.variant_id.toString() === variantId);
  }

  async function removeExistingFreeItems() {
    for (const variantId of FREE_ITEM_IDS) {
      if (await isItemInCart(variantId)) await removeItem(variantId);
    }
  }

  /**
   * Which free gift should be in the cart at checkout time.
   * Delegates to the shared matcher so this can never drift away from the
   * drawer display again. See FREE_GIFT config at the top of this file.
   */
  function determineFreeItemVariantId(cartData) {
    const tier = giftTierForCart(cartData);
    if (tier === 'pack') return FREE_GIFT.PACK_VARIANT_ID;   // Free Eureka Welcome Pack
    if (tier === 'treat') return FREE_GIFT.TREAT_VARIANT_ID; // Free Eureka Welcome Treat
    return null;
  }

  async function removeItem(variantId) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 }),
      });
      if (!response.ok) throw new Error(`Failed to remove item ${variantId} from cart: ${response.statusText}`);
    } catch (error) { /* swallow */ }
  }

  // Theme-side cart remove buttons (NOT Upcart) — reload after remove
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('cart-remove-button button').forEach((button) => {
      button.addEventListener('click', handleRemoveButtonClick);
    });
  });

  async function handleRemoveButtonClick(event) {
    const cartData = await fetchCartData();
    if (cartData) await checkAndRemoveItems(cartData);
    window.location.reload();
  }

  async function checkAndRemoveItems(cartData) {
    const variantIds = ['46681050611996', '46673711595804', '46673711628572', '46460357869852', '46322042798364'];
    for (const item of cartData.items) {
      if (variantIds.includes(item.variant_id.toString())) {
        await removeItem(item.variant_id.toString());
      }
    }
  }

  // Legacy updateCart() kept for backward compatibility — NOT used by current checkout
  async function updateCart(freeItemVariantId) {
    return executeWithUserInteraction(async () => {
      try {
        const isFreeItemInCart = await isItemInCart(freeItemVariantId);
        if (!isFreeItemInCart) await addFreeItemDirect(freeItemVariantId);
      } catch (error) { /* swallow */ }

      const refreshDelay = isIPhone() ? 1000 : 500;
      setTimeout(() => {
        if (window.upcartRefreshCart) window.upcartRefreshCart();
      }, refreshDelay);
    });
  }


  // ════════════════════════════════════════════════════════════════════════════
  // ██  PART 2: CART MESSAGE / TASTE TESTER LOGIC  ██
  // ════════════════════════════════════════════════════════════════════════════

  (function cartMessageForTasteTester() {
    var debounceTimer;
    var originalMessage = null;
    var drawerWasOpen = false;
    var firstCartAdd = true;

    // CSS — hide Upcart default rewards (both V1 and V2), fade custom one in
    var style = document.createElement('style');
    style.textContent = '' +
      '.upcart-rewards.styles_Rewards__, .upcart-public-rewards { display: none !important; }' +
      '#sg_upcart_drawer_free_ship_msg .upcart-rewards,' +
      '#sg_upcart_drawer_free_ship_msg .upcart-public-rewards { opacity: 0; transition: opacity 0.4s ease; display: block !important; }' +
      '#sg_upcart_drawer_free_ship_msg .upcart-rewards.sg-ready,' +
      '#sg_upcart_drawer_free_ship_msg .upcart-public-rewards.sg-ready { opacity: 1; }' +
      '#sg-shipping-loader { text-align: center; padding: 14px 0 18px; }' +
      '#sg-shipping-loader p { font-size: 13px; color: rgb(48,48,48); font-weight: 400; margin: 0 0 10px; letter-spacing: 0.3px; }' +
      '#sg-shipping-loader .sg-loader-bar { height: 6px; border-radius: 3px; background: rgb(235,235,235); overflow: hidden; margin: 0 20px; }' +
      '#sg-shipping-loader .sg-loader-fill { height: 100%; width: 40%; border-radius: 3px; background: rgb(250,174,80); animation: sgSlide 1.2s ease-in-out infinite; }' +
      '@keyframes sgSlide { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }';
    document.head.appendChild(style);

    var loaderHTML = '' +
      '<div id="sg-shipping-loader">' +
      '  <p>Calculating Shipping...</p>' +
      '  <div class="sg-loader-bar"><div class="sg-loader-fill"></div></div>' +
      '</div>';

    function showLoading() {
      var customContainer = document.getElementById('sg_upcart_drawer_free_ship_msg');
      if (!customContainer) return;
      var reward = customContainer.querySelector(UPCART_SEL.rewards);
      if (reward) reward.classList.remove('sg-ready');
      if (!document.getElementById('sg-shipping-loader')) {
        customContainer.insertAdjacentHTML('afterbegin', loaderHTML);
      }
    }

    function hideLoading() {
      var loader = document.getElementById('sg-shipping-loader');
      if (loader) loader.remove();
      var customContainer = document.getElementById('sg_upcart_drawer_free_ship_msg');
      if (customContainer) {
        var reward = customContainer.querySelector(UPCART_SEL.rewards);
        if (reward) reward.classList.add('sg-ready');
      }
    }

    function showLoadingWithRetry() {
      var retryCount = 0;
      var retryInterval = setInterval(function () {
        retryCount++;
        var container = document.getElementById('sg_upcart_drawer_free_ship_msg');
        if (container) {
          clearInterval(retryInterval);
          showLoading();
          waitForProductsThenReveal();
        }
        if (retryCount > 50) clearInterval(retryInterval);
      }, 100);
    }

    function waitForProductsThenReveal() {
      var checkCount = 0;
      var checker = setInterval(function () {
        checkCount++;
        var items = document.querySelectorAll(UPCART_SEL.productItem);
        if (items.length > 0 || checkCount > 15) {
          clearInterval(checker);
          setTimeout(function () {
            updateRewardsMessage();
            hideLoading();
          }, 300);
        }
      }, 200);
    }

    function updateRewardsMessage() {
      var customContainer = document.getElementById('sg_upcart_drawer_free_ship_msg');
      if (!customContainer) return;

      var rewardsMessage = customContainer.querySelector(UPCART_SEL.rewardsMessage);
      if (!rewardsMessage) return;

      if (originalMessage === null && rewardsMessage.innerHTML.indexOf('Your sample is on its way') === -1) {
        originalMessage = rewardsMessage.innerHTML;
      }

      var cartItems = document.querySelectorAll(UPCART_SEL.productItem);
      var rewardsBar = customContainer.querySelector(UPCART_SEL.rewardsBar);
      var tiersContainer = customContainer.querySelector(UPCART_SEL.rewardsTiersContainer);

      var isSampleOnly = false;

      if (cartItems.length === 1) {
        var titleEl = cartItems[0].querySelector(UPCART_SEL.productTitleLink) ||
          cartItems[0].querySelector(UPCART_SEL.productTitle);
        if (titleEl && titleEl.textContent.trim() === 'Taste Tester') {
          isSampleOnly = true;
        }
      }

      if (isSampleOnly) {
        rewardsMessage.innerHTML = '<p style="font-weight: 300;"><strong>Your sample is on its way!</strong></p>';
        if (rewardsBar) rewardsBar.style.display = 'none';
        if (tiersContainer) tiersContainer.style.display = 'none';
      } else {
        if (originalMessage && rewardsMessage.innerHTML.indexOf('Your sample is on its way') !== -1) {
          rewardsMessage.innerHTML = originalMessage;
        }
        if (rewardsBar) rewardsBar.style.display = '';
        if (tiersContainer) tiersContainer.style.display = '';
      }
    }

    function debouncedUpdate() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(updateRewardsMessage, 300);
    }

    var observersAttached = false;
    function attachObservers() {
      if (observersAttached) return;
      var cartBody = document.querySelector(UPCART_SEL.cartBody);
      if (cartBody) {
        observersAttached = true;
        new MutationObserver(debouncedUpdate).observe(cartBody, { childList: true, subtree: true });
      }
    }

    // ────────────────────────────────────────────────────────────────────────
    // Cart drawer open/close detection — V1/V2 COMPATIBLE
    //   PRIMARY:  window.upcartOnCartOpen / upcartOnCartClosed API hooks
    //   FALLBACK: visibility-based detection (computed style + bounding rect)
    // ────────────────────────────────────────────────────────────────────────
    function onDrawerOpen() {
      if (!drawerWasOpen) {
        drawerWasOpen = true;
        showLoading();
        attachObservers();
        waitForProductsThenReveal();
      }
    }

    function onDrawerClose() {
      if (drawerWasOpen) {
        drawerWasOpen = false;
        var loader = document.getElementById('sg-shipping-loader');
        if (loader) loader.remove();
        var customContainer = document.getElementById('sg_upcart_drawer_free_ship_msg');
        if (customContainer) {
          var reward = customContainer.querySelector(UPCART_SEL.rewards);
          if (reward) reward.classList.remove('sg-ready');
        }
        // Also reset overflow when drawer closes
        removeOverflowHidden();
      }
    }

    // PRIMARY: documented Upcart API hooks (preserve any existing handlers)
    var existingOnClosed = window.upcartOnCartClosed;
    window.upcartOnCartClosed = function () {
      if (typeof existingOnClosed === 'function') {
        try { existingOnClosed.apply(this, arguments); } catch (e) { }
      }
      onDrawerClose();
    };

    var existingOnOpen = window.upcartOnCartOpen;
    window.upcartOnCartOpen = function () {
      if (typeof existingOnOpen === 'function') {
        try { existingOnOpen.apply(this, arguments); } catch (e) { }
      }
      onDrawerOpen();
    };

    // FALLBACK: visibility observer on #CartPopup
    function watchCartDrawer() {
      var cartPopup = document.querySelector(UPCART_SEL.cartPopup);
      if (!cartPopup) return;

      function isVisible(el) {
        if (!el) return false;
        var cs = window.getComputedStyle(el);
        if (cs.display === 'none' || cs.visibility === 'hidden') return false;
        var rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return false;
        return true;
      }

      new MutationObserver(function () {
        var isOpen = isVisible(cartPopup);
        if (isOpen && !drawerWasOpen) onDrawerOpen();
        if (!isOpen && drawerWasOpen) onDrawerClose();
      }).observe(cartPopup, {
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden', 'data-state', 'hidden']
      });
    }

    // Intercept Shopify cart endpoints to trigger rewards updates
    // Note: Part 1 also wraps window.fetch — we wrap it again here. Order doesn't
    // matter because both wrappers preserve the original via closure.
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var result = originalFetch.apply(this, arguments);
      if (typeof url === 'string' && (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/change') !== -1 || url.indexOf('/cart/update') !== -1 || url.indexOf('/cart/clear') !== -1)) {
        if (firstCartAdd && url.indexOf('/cart/add') !== -1) {
          firstCartAdd = false;
          showLoadingWithRetry();
        }
        if (result && typeof result.then === 'function') {
          result.then(function () {
            attachObservers();
            setTimeout(updateRewardsMessage, 800);
            setTimeout(updateRewardsMessage, 1500);
          });
        }
      }
      return result;
    };

    // Poll briefly for CartPopup to appear, then attach watcher
    var poll = setInterval(function () {
      var cartPopup = document.querySelector(UPCART_SEL.cartPopup);
      if (cartPopup) {
        clearInterval(poll);
        var cs = window.getComputedStyle(cartPopup);
        var rect = cartPopup.getBoundingClientRect();
        drawerWasOpen = cs.display !== 'none' && cs.visibility !== 'hidden' && (rect.width > 0 || rect.height > 0);
        watchCartDrawer();
      }
    }, 200);

    setTimeout(function () { clearInterval(poll); }, 10000);

    setTimeout(function () {
      updateRewardsMessage();
      attachObservers();
      setTimeout(updateRewardsMessage, 500);
      setTimeout(updateRewardsMessage, 1500);
    }, 1000);
  })();

})();